import defaultLanguage from "@/locales/en.json";
import { Europafacts } from "@/services/api";
import type { SupportedLanguage, TranslationKeys } from "@/services/locale";
import { i18n } from "@/services/locale";
import type { DatasetLanguage, Language } from "@/types/europafacts";
import type { LanguageStore } from "@/types/stores/language";
import { createAsyncLoader } from "@/utils/stores/asyncLoader";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

const preloadedImages = new Set<string>();

function preloadFlagImages(languages: Language[]): void {
  languages.forEach((language) => {
    if (language.flag_file && !preloadedImages.has(language.flag_file)) {
      const img = new window.Image();
      img.src = language.flag_file;
      preloadedImages.add(language.flag_file);
    }
  });
}

const initialState = {
  currentLanguage: i18n.getCurrentLanguage(),
  isLoading: true,
  translations: {} as Record<SupportedLanguage, TranslationKeys>,
  languages: [],
  isLoadingLanguages: false,
  languagesPromise: null,
};

export const useLanguageStore = create<LanguageStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        t: <K extends keyof TranslationKeys>(key: K): TranslationKeys[K] => {
          const translation = i18n.t(key);
          return translation ?? (defaultLanguage[key] as TranslationKeys[K]);
        },

        setLanguage: async (language: SupportedLanguage) => {
          await i18n.setLanguage(language);
          set({ currentLanguage: language }, false, "language/setLanguage");
        },

        loadTranslations: async () => {
          try {
            await i18n.loadTranslations();
            const currentLanguage = i18n.getCurrentLanguage();
            set(
              { isLoading: false, currentLanguage },
              false,
              "language/loadTranslations"
            );
          } catch (error) {
            set({ isLoading: false }, false, "language/loadTranslations-error");
          }
        },

        loadLanguages: createAsyncLoader<
          DatasetLanguage[],
          Language[],
          LanguageStore
        >({
          loadFn: () => Europafacts.getLanguages(),
          dataKey: "languages",
          loadingKey: "isLoadingLanguages",
          promiseKey: "languagesPromise",
          actionName: "language/loadLanguages",
          transform: (datasetLanguages: DatasetLanguage[]) => {
            const filtered = datasetLanguages.filter(
              (language) => language.flag_file
            );
            const languages: Language[] = filtered.map(
              (dl: DatasetLanguage) => ({
                language_id: dl.iso2,
                language_name: dl.language_name,
                language_name_original: dl.language_name,
                iso2: dl.iso2,
                flag_file: dl.flag_file,
                description: null,
                is_locale_active: true,
                datetime_created: new Date().toISOString(),
                datetime_updated: new Date().toISOString(),
              })
            );
            preloadFlagImages(languages);
            return languages;
          },
        })(set, get),
      }),
      {
        name: "language-store",
        partialize: (state) => ({
          currentLanguage: state.currentLanguage,
        }),
      }
    ),
    {
      name: "language-store",
    }
  )
);

i18n.subscribe(() => {
  const currentLanguage = i18n.getCurrentLanguage();
  useLanguageStore.setState({ currentLanguage }, false, "language/subscribe");
});

export const useCurrentLanguage = () =>
  useLanguageStore((state) => state.currentLanguage);

export const useIsLoadingLanguage = () =>
  useLanguageStore((state) => state.isLoading);

export const useT = () => useLanguageStore((state) => state.t);

export const useSetLanguage = () =>
  useLanguageStore((state) => state.setLanguage);

export const useLoadTranslations = () =>
  useLanguageStore((state) => state.loadTranslations);

export const useLanguages = () => useLanguageStore((state) => state.languages);

export const useIsLoadingLanguages = () =>
  useLanguageStore((state) => state.isLoadingLanguages);

export const useLoadLanguages = () =>
  useLanguageStore((state) => state.loadLanguages);
